import React, { useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { 
  CheckCircle2, 
  Sparkles, 
  PhoneCall, 
  MessageCircle, 
  Printer, 
  ArrowRight,
  Package,
  Clock,
  ShieldCheck
} from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const location = useLocation();
  const { settings } = useSiteSettings();
  const [order] = useState(location.state?.order || null);

  // We intentionally do NOT re-fetch the order by ID here — order details contain
  // customer PII and are only accessible to admins via GET /api/orders/{id} or
  // to the customer themselves via POST /api/orders/track (phone-verified).
  // If a customer refreshes/loses this page state, they can look it up on
  // /track-order using their order number + phone.

  if (!order) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center space-y-4">
        <h2 className="font-heading text-2xl font-bold text-stone-900">Order receipt not available</h2>
        <p className="text-xs text-stone-500 max-w-sm mx-auto">
          For your privacy, order details are not shared through the browser URL. To view your order,
          please use our secure Order Tracking page with your order number and mobile.
        </p>
        <div className="flex justify-center gap-2">
          <Link to="/track-order" data-testid="order-conf-track-btn" className="px-5 py-2 bg-[#1C1917] text-white text-xs font-bold rounded-lg inline-block">
            Track My Order
          </Link>
          <Link to="/" className="px-5 py-2 bg-stone-100 text-stone-800 text-xs font-bold rounded-lg inline-block">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const handleWhatsAppNotify = () => {
    const phone = settings.whatsapp_number.replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(
      `Assalam o Alaikum! I have placed an order on Anwer Fancy Lace.%0A` +
      `*Order ID:* ${order.order_number}%0A` +
      `*Customer:* ${order.customer_name}%0A` +
      `*City:* ${order.city}%0A` +
      `*Total:* Rs. ${order.total_amount?.toLocaleString()} PKR%0A` +
      `Please confirm dispatch status.`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-8">
      
      {/* Confirmation Hero Card */}
      <div 
        data-testid="order-confirmation-receipt"
        className="bg-white rounded-3xl border-2 border-[#D4AF37]/50 p-6 sm:p-10 shadow-2xl space-y-6 text-center relative overflow-hidden"
      >
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto shadow-inner">
          <CheckCircle2 className="w-10 h-10" />
        </div>

        <div className="space-y-1">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#996515]">
            Thank You for Choosing Anwer Fancy Lace
          </span>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-stone-900">
            Your Order is Confirmed!
          </h1>
          <p className="text-xs text-stone-500 max-w-md mx-auto pt-1">
            We have received your lace set order. Our packing team in Lahore will dispatch your package shortly.
          </p>
        </div>

        {/* Order Number Badge */}
        <div className="bg-[#FAF8F5] p-4 rounded-xl border border-[#E5E0D8] inline-block">
          <span className="text-xs text-stone-400 block font-normal">Order Reference Number</span>
          <span 
            data-testid="confirmed-order-number"
            className="font-heading text-xl font-bold text-stone-900 tracking-wider"
          >
            {order.order_number}
          </span>
        </div>

        {/* Order Details Breakdown */}
        <div className="text-left bg-[#FAF8F5] p-5 rounded-2xl border border-[#E5E0D8] space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-4 pb-3 border-b border-stone-200">
            <div>
              <span className="text-stone-400 block">Customer Name</span>
              <span className="font-bold text-stone-900">{order.customer_name}</span>
            </div>
            <div>
              <span className="text-stone-400 block">Contact / WhatsApp</span>
              <span className="font-bold text-stone-900">{order.mobile_number}</span>
            </div>
            <div>
              <span className="text-stone-400 block">Delivery City</span>
              <span className="font-bold text-stone-900">{order.city}</span>
            </div>
            <div>
              <span className="text-stone-400 block">Payment Method</span>
              <span className="font-bold text-stone-900 uppercase">{order.payment_method}</span>
            </div>
          </div>

          <div>
            <span className="text-stone-400 block mb-1">Delivery Address</span>
            <span className="font-medium text-stone-800">{order.delivery_address}</span>
          </div>

          {/* Items in Receipt */}
          <div className="space-y-2 pt-2 border-t border-stone-200">
            <span className="font-bold text-stone-900 block uppercase tracking-wider text-[10px]">
              Ordered Complete Sets:
            </span>
            {order.items && order.items.map((it, idx) => (
              <div key={idx} className="flex justify-between items-center py-1 border-b border-stone-200/60 last:border-0">
                <div>
                  <span className="font-semibold text-stone-900">{it.product_name}</span>
                  <span className="text-stone-500 block text-[11px]">{it.quantity}x Set ({it.selected_color || "Set Pack"})</span>
                </div>
                <span className="font-bold text-stone-900">Rs. {it.item_total?.toLocaleString()}</span>
              </div>
            ))}
          </div>

          {/* Grand Total */}
          <div className="pt-3 border-t border-stone-300 flex justify-between items-center text-sm font-bold text-stone-900">
            <span>Total Amount (PKR)</span>
            <span className="font-heading text-lg text-[#996515]">Rs. {order.total_amount?.toLocaleString()}</span>
          </div>
        </div>

        {/* Next Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <button
            type="button"
            onClick={handleWhatsAppNotify}
            data-testid="order-track-whatsapp-btn"
            className="flex-1 py-3 px-4 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-xl shadow-md flex items-center justify-center gap-2 transition"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Notify us on WhatsApp</span>
          </button>

          <Link
            to="/shop"
            className="flex-1 py-3 px-4 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-md flex items-center justify-center gap-2 transition"
          >
            <span>Continue Shopping</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

      </div>

    </div>
  );
}
